/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useContext, useState } from 'react';
import { Button, Form, Input, Modal, Select } from 'antd';
import dayjs from 'dayjs';

import { EMPTY_ID } from '@totem/components/common/reference/ReferenceSelector';
import TicketContext from '@totem/components/ticketing/details/ticketContext';
import { PartyInvolved } from '@totem/components/ticketing/ticketing';
import ModalFooter from '@totem/components/ui_components/dialogs/ModalFooter';
import ModalTitle from '@totem/components/ui_components/dialogs/ModalTitle';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';
import { TICKET_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';
import { isInternalEmailAddress } from '@totem/utilities/security';
const PartiesInvolvedAddDialog = () => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const { ticket, onAction, action } = useContext(TicketContext);
  const [isSending, setIsSending] = useState(false);
  const getPartyInvolvedOptions = () => {
    if (isNotNull(ticket) && isNotNull(ticket.ticket)) {
      switch (ticket.ticket.type) {
        case 'Project':
          // eslint-disable-next-line no-case-declarations
          const options = [
            'Project Manager',
            'Project Sponsor',
            'Internal Project Resource',
            'Watcher',
          ];
          if (!ticket.ticket.internalOnly) {
            options.push('Customer Resource');

            options.push('Vendor Resource');
          }
          return options;
        case 'HR':
          // eslint-disable-next-line no-case-declarations
          const hrOptions = [
            'Project Manager',
            'HR Admin',
            'Hiring Manager',
            'Department Lead',
            'Employee',
          ];
          return hrOptions;
        default:
          return ['Assignee', 'Watcher'];
      }
    }
    return ['Assignee', 'Watcher'];
  };
  const partyInvolvedTypeOptions = getPartyInvolvedOptions();
  const [partyInvolvedData, setPartyInvolvedData] = useState<PartyInvolved>({
    id: EMPTY_ID,
    type: partyInvolvedTypeOptions[0],
    status: 'ACTIVE',
    notify: true,
    name: '',
    email: '',
    phone: '',
    involvementStart: dayjs().toISOString(),
    involvementEnd: '0001-01-01T00:00:00.000+00:00',
    createdByType: 'USER',
    createdByName: userProfile.email,
    createdById: userProfile.id,
    keys: [],
  });
  const [form] = Form.useForm();
  const sendPartyInvolved = useCallback(
    async (paramTicketId: string, payload: PartyInvolved) => {
      if (!isSending) {
        fetch(`${TICKET_ENDPOINT}/${paramTicketId}/partyInvolved`, {
          method: 'POST',
          headers: new Headers({
            Authorization: `Bearer ${getApiToken()}`,
          }),
          body: JSON.stringify(payload),
        }).then((res) => {
          setIsSending(false);
          const success = CheckResponseShowError(res);
          if (success) {
            form.resetFields();

            onAction('refresh', '');
          }
        });
      }
    }, // End async
    [],
  );
  const handleSubmit = () => {
    form.validateFields().then(() => {
      sendPartyInvolved(ticket.ticket.id, partyInvolvedData);
    });
  };
  return (
    <Modal
      open={action === 'show-party-involved-entry'}
      onCancel={() => onAction('', '')}
      title={<ModalTitle>Add Party Involved</ModalTitle>}
      confirmLoading={false}
      width={'40%'}
      footer={
        <ModalFooter>
          <Button onClick={() => onAction('', '')}>Close</Button>
          <Button type="primary" onClick={handleSubmit}>
            Save
          </Button>
        </ModalFooter>
      }
    >
      <Form.Item
        label="Involvement Type"
        name="involvementType"
        initialValue={partyInvolvedData.type}
        rules={[
          { required: true, message: 'Please enter at least one dependency' },
        ]}
      >
        <Select
          placeholder="Select Involvement Type"
          value={partyInvolvedData.type}
          onChange={(value) =>
            setPartyInvolvedData((prevState) => ({
              ...prevState,
              type: value,
            }))
          }
        >
          {partyInvolvedTypeOptions.map((piv) => (
            <Select.Option key={piv} value={piv}>
              {`${piv}`}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item
        label="Name"
        name="name"
        initialValue={partyInvolvedData.name}
        rules={[{ required: true, message: 'Please enter name' }]}
      >
        <Input
          value={partyInvolvedData.name}
          onChange={(event) =>
            setPartyInvolvedData((prevState) => ({
              ...prevState,
              name: event.target.value,
            }))
          }
        />
      </Form.Item>
      <Form.Item
        label="Email"
        name="email"
        initialValue={partyInvolvedData.email}
        rules={[
          { required: true, message: 'Please enter email address' },
          {
            type: 'email',
            message:
              'Can only add internal users as parties involved to internal tickets',
          },
          () => ({
            validator(_, value) {
              if (!ticket.ticket.internalOnly) {
                return Promise.resolve();
              }
              if (isInternalEmailAddress(partyInvolvedData.email)) {
                return Promise.resolve();
              }
              return Promise.reject(
                new Error(
                  'Email must be internal user when on internal only ticket.',
                ),
              );
            },
          }),
        ]}
      >
        <Input
          value={partyInvolvedData.email}
          onChange={(event) =>
            setPartyInvolvedData((prevState) => ({
              ...prevState,
              email: event.target.value,
            }))
          }
        />
      </Form.Item>
      <Form.Item
        label="Phone"
        name="phone"
        initialValue={partyInvolvedData.phone}
      >
        <Input
          value={partyInvolvedData.phone}
          onChange={(event) =>
            setPartyInvolvedData((prevState) => ({
              ...prevState,
              phone: event.target.value,
            }))
          }
        />
      </Form.Item>
      {/*<Form.Item*/}
      {/*  label="Notify"*/}
      {/*  name="notify"*/}
      {/*  initialValue={partyInvolvedData.notify}*/}
      {/*>*/}
      {/*  <Switch*/}
      {/*    onChange={(isChecked: boolean) =>*/}
      {/*      setPartyInvolvedData((prevState) => ({*/}
      {/*        ...prevState,*/}
      {/*        notify: isChecked,*/}
      {/*      }))*/}
      {/*    }*/}
      {/*  />*/}
      {/*</Form.Item>*/}
    </Modal>
  );
};
export default PartiesInvolvedAddDialog;
